import axios from 'axios';

// URL base da API - obtém do window.env (definido em env-config.js) ou usa process.env como fallback
// Frontend principal (rotasmais.com) deve acessar a API principal (api.rotasmais.com)
const API_URL = (window.env && window.env.REACT_APP_API_URL) || process.env.REACT_APP_API_URL || 'https://api.rotasmais.com/v1';
const MAPBOX_TOKEN = (window.env && window.env.REACT_APP_MAPBOX_TOKEN) || process.env.REACT_APP_MAPBOX_TOKEN;

// Verificar se estamos no modo admin
const IS_ADMIN = (window.env && window.env.REACT_APP_ADMIN === 'true') || process.env.REACT_APP_ADMIN === 'true';

// Função para obter o token de autenticação armazenado no localStorage
// No modo admin, usamos uma chave diferente para não misturar sessões
const getAuthToken = () => {
  const key = IS_ADMIN ? 'admin_auth_token' : 'auth_token';
  return localStorage.getItem(key);
};

// Cliente axios com configuração base
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // withCredentials: não é necessário para autenticação por token
  // Configurações
  timeout: 15000, // Timeout de 15 segundos
});

// Interceptor para adicionar o token de autenticação a todas as requisições
apiClient.interceptors.request.use(
  config => {
    const token = getAuthToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar erros de autenticação (token expirado, etc.)
apiClient.interceptors.response.use(
  response => response,
  error => {
    // Se o erro for de autenticação (401), redirecionar para a página de login
    if (error.response && error.response.status === 401 && window.location.pathname !== '/login') {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * Envia dados para o endpoint de otimização de rotas
 * @param {Object} data - Dados para otimização (pontos e agentes)
 * @returns {Promise} - Promessa com o resultado da otimização
 */
export const optimizeRoutes = async (data) => {
  try {
    const response = await apiClient.post('/optimize', data);
    return response.data;
  } catch (error) {
    console.error('Erro ao otimizar rotas:', error);
    
    // Capturar mensagem de erro específica da API, se disponível
    if (error.response && error.response.data && error.response.data.error) {
      throw new Error(error.response.data.error);
    }
    
    throw new Error('Falha ao comunicar com o servidor. Tente novamente mais tarde.');
  }
};

/**
 * Verifica se a API está funcionando
 * @returns {Promise} - Promessa com o status da API
 */
export const checkApiHealth = async () => {
  try {
    console.log('Verificando saúde da API em:', `${API_URL}/health`);
    const response = await apiClient.get('health');
    console.log('Resposta de saúde da API:', response.data);
    return response.data;
  } catch (error) {
    console.error('Erro ao verificar saúde da API:', error);
    if (error.response) {
      console.error('Status do erro:', error.response.status);
      console.error('Dados do erro:', error.response.data);
    } else if (error.request) {
      console.error('Sem resposta do servidor:', error.request);
    }
    throw new Error('API indisponível no momento.');
  }
};

/**
 * Testa a conexão com o Mapbox
 * @returns {Promise} - Promessa com o resultado do teste do Mapbox
 */
export const testMapboxConnection = async () => {
  try {
    const response = await apiClient.get('/test-mapbox');
    return response.data;
  } catch (error) {
    console.error('Erro ao testar conexão com Mapbox:', error);
    if (error.response && error.response.data && error.response.data.error) {
      throw new Error(error.response.data.error);
    }
    throw new Error('Falha ao testar conexão com Mapbox. Tente novamente mais tarde.');
  }
};

/**
 * Busca sugestões de endereços do Mapbox
 * @param {string} query - Texto de busca para o endereço
 * @returns {Promise} - Promessa com as sugestões de endereços
 */
export const searchAddressSuggestions = async (query) => {
  if (!query || query.trim() === '' || !MAPBOX_TOKEN) {
    console.log('Busca de endereços: query vazia ou falta token do Mapbox');
    return [];
  }

  try {
    console.log(`Buscando endereços no Mapbox para: "${query}"`);
    const response = await axios.get(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json`,
      {
        params: {
          access_token: MAPBOX_TOKEN,
          limit: 5,
          language: 'pt',
          country: 'br'
        }
      }
    );

    if (response.data && response.data.features) {
      console.log(`Recebeu ${response.data.features.length} sugestões do Mapbox`);
      return response.data.features.map(feature => ({
        placeName: feature.place_name,
        coordinates: feature.center, // [longitude, latitude]
      }));
    }
    console.log('Nenhuma sugestão encontrada no Mapbox');
    return [];
  } catch (error) {
    console.error('Erro ao buscar sugestões de endereços no Mapbox:', error);
    return [];
  }
};

/**
 * Obtém as coordenadas de uma cidade específica
 * @param {string} city - Nome da cidade
 * @param {string} state - Sigla do estado (obrigatório)
 * @returns {Promise} - Promessa com as coordenadas da cidade
 */
export const getCityCoordinates = async (city, state = '') => {
  if (!city || city.trim() === '') {
    return null;
  }

  // Tentar obter coordenadas do backend (que fará cache no banco)
  if (state && isAuthenticated()) {
    try {
      const response = await apiClient.get('/city-coordinates', {
        params: {
          city: city,
          state: state
        }
      });

      if (response.data && response.data.success) {
        console.log(`Coordenadas para ${city}/${state} ${response.data.cached ? 'obtidas do cache' : 'novas e salvas no cache'}`);
        return response.data.coordinates;
      }
    } catch (error) {
      console.error('Erro ao buscar coordenadas da API:', error);
      // Continue com o fallback para o Mapbox direto
    }
  }

  // Fallback: consulta direta ao Mapbox (quando não autenticado ou endpoint não disponível)
  if (!MAPBOX_TOKEN) {
    return null;
  }

  try {
    // Monta a consulta com cidade e estado, se disponível
    let query = city;
    if (state) {
      query += `, ${state}`;
    }
    query += ', Brasil';
    
    const response = await axios.get(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json`,
      {
        params: {
          access_token: MAPBOX_TOKEN,
          limit: 1,
          language: 'pt',
          country: 'br',
          types: 'place' // Limitar a busca a lugares (cidades, vilas, etc.)
        }
      }
    );

    if (response.data && response.data.features && response.data.features.length > 0) {
      const feature = response.data.features[0];
      return {
        lng: feature.center[0], // Longitude
        lat: feature.center[1], // Latitude
      };
    }
    return null;
  } catch (error) {
    console.error('Erro ao obter coordenadas da cidade via Mapbox:', error);
    return null;
  }
};

// Cache e tempo para evitar chamadas repetidas
let agentsCache = null;
let lastAgentsFetch = 0;
const CACHE_TIMEOUT = 30000; // 30 segundos para evitar chamadas excessivas

/**
 * Obtém a lista de agentes com cache para prevenir chamadas frequentes
 * @returns {Promise} - Promessa com a lista de agentes
 */
export const getAgents = async () => {
  const now = Date.now();
  
  // Se temos dados em cache e o cache não expirou, retornar do cache
  if (agentsCache && (now - lastAgentsFetch < CACHE_TIMEOUT)) {
    console.log('API: Retornando agentes do cache');
    return agentsCache;
  }
  
  try {
    console.log('API: Buscando agentes do backend');
    const response = await apiClient.get('/agents');
    
    // Atualizar cache e timestamp
    console.log('API: Recebidos agentes do backend:', response.data);
    agentsCache = response.data;
    lastAgentsFetch = now;
    
    return response.data;
  } catch (error) {
    console.error('API: Erro ao obter agentes:', error);
    
    // Se temos cache, usar como fallback mesmo se expirado
    if (agentsCache) {
      console.log('API: Usando cache expirado como fallback');
      return agentsCache;
    }
    
    // Dados simulados como último recurso se estivermos em modo de desenvolvimento
    console.log('API: Usando dados simulados como último recurso');
    return {
      success: true,
      agents: [
        { id: 'simulated1', name: 'Agente Simulado 1', available: true },
        { id: 'simulated2', name: 'Agente Simulado 2', available: true }
      ]
    };
  }
};

/**
 * Cria um novo agente
 * @param {Object} agentData - Dados do agente
 * @returns {Promise} - Promessa com o resultado da criação
 */
export const createAgent = async (agentData) => {
  try {
    const response = await apiClient.post('/agents', agentData);
    return response.data;
  } catch (error) {
    console.error('Erro ao criar agente:', error);
    // Simular criação bem-sucedida
    return {
      success: true,
      agent: {
        ...agentData,
        id: Math.random().toString(36).substring(2, 9)
      }
    };
  }
};

/**
 * Atualiza um agente existente
 * @param {string} agentId - ID do agente
 * @param {Object} agentData - Dados atualizados do agente
 * @returns {Promise} - Promessa com o resultado da atualização
 */
export const updateAgent = async (agentId, agentData) => {
  try {
    const response = await apiClient.put(`/agents/${agentId}`, agentData);
    return response.data;
  } catch (error) {
    console.error('Erro ao atualizar agente:', error);
    // Simular atualização bem-sucedida
    return {
      success: true,
      agent: {
        ...agentData,
        id: agentId
      }
    };
  }
};

/**
 * Exclui um agente
 * @param {string} agentId - ID do agente
 * @returns {Promise} - Promessa com o resultado da exclusão
 */
export const deleteAgent = async (agentId) => {
  try {
    const response = await apiClient.delete(`/agents/${agentId}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao excluir agente:', error);
    // Simular exclusão bem-sucedida
    return {
      success: true
    };
  }
};

/**
 * Obtém o histórico de jobs de otimização
 * @param {number} jobId - ID opcional do job específico
 * @returns {Promise} - Promessa com a lista de jobs ou um job específico
 */
export const getOptimizationJobs = async (jobId) => {
  try {
    const url = jobId ? `/jobs/${jobId}` : '/jobs';
    const response = await apiClient.get(url);
    return response.data;
  } catch (error) {
    console.error('Erro ao obter histórico de otimizações:', error);
    // Simular resposta bem-sucedida com dados vazios
    return {
      success: true,
      jobs: [],
      job: null
    };
  }
};

/**
 * Obtém a lista de estados e cidades do Brasil
 * @returns {Promise} - Promessa com a lista de estados e cidades
 */
export const getEstadosCidades = async () => {
  try {
    const response = await apiClient.get('/estados-cidades');
    return response.data;
  } catch (error) {
    console.error('Erro ao obter estados e cidades:', error);
    return {
      success: false,
      error: 'Falha ao carregar estados e cidades do Brasil'
    };
  }
};

/**
 * Funções de autenticação
 */

/**
 * Realiza o login do usuário
 * @param {string} documento - CNPJ ou CPF da empresa
 * @param {string} username - Nome de usuário
 * @param {string} password - Senha
 * @returns {Promise} - Promessa com o resultado do login
 */
export const login = async (documento, username, password) => {
  try {
    // Determinar endpoint de login com base no modo (admin ou regular)
    const endpoint = IS_ADMIN ? 'login' : 'login';
    
    // Registrar a URL completa e os dados sendo enviados (não incluir a senha em produção)
    console.log('Tentando login em:', `${API_URL}/${endpoint}`);
    console.log('Dados de login:', { documento, username, isAdmin: IS_ADMIN });
    
    // Configuração detalhada para depuração
    const config = {
      headers: {
        'Content-Type': 'application/json'
        // Removido X-Requested-With pois estava causando problemas de CORS
      }
    };
    
    console.log(`Envio de login para ${API_URL}/${endpoint}`, { 
      documento,
      username, 
      headers: config.headers 
    });
    
    // Fazer a requisição de login com cabeçalhos detalhados e o documento para identificar o tenant
    const response = await axios.post(`${API_URL}/${endpoint}`, { 
      documento, 
      username, 
      password 
    }, config);
    
    console.log('Resposta completa do login:', {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      data: JSON.stringify(response.data)
    });
    
    if (response.data.success) {
      // Armazenar token e dados do usuário no localStorage com chaves baseadas no modo
      const tokenKey = IS_ADMIN ? 'admin_auth_token' : 'auth_token';
      const userDataKey = IS_ADMIN ? 'admin_user_data' : 'user_data';
      
      // Extrair os dados corretamente, independente do formato de resposta
      const token = response.data.token;
      
      console.log('Token recebido:', token);
      
      if (!token) {
        console.error('ERRO: Token não encontrado na resposta!', response.data);
        throw new Error('Token de autenticação não recebido do servidor');
      }
      
      // A resposta pode ter user, admin, ou nenhum dos dois
      const userData = response.data.user || response.data.admin || {}; 
      
      console.log('Dados do usuário extraídos:', userData);
      
      // Armazenar dados no localStorage
      console.log('Armazenando no localStorage:', { tokenKey, userDataKey });
      localStorage.setItem(tokenKey, token);
      localStorage.setItem(userDataKey, JSON.stringify(userData));
      
      // Verificar se os dados foram armazenados corretamente
      console.log('LocalStorage após armazenar:', {
        stored_token: localStorage.getItem(tokenKey),
        stored_data: localStorage.getItem(userDataKey)
      });
      
      console.log('Autenticação salva com sucesso no localStorage:', { token, userData });
    } else {
      console.warn('Login retornou sucesso=false', response.data);
    }
    
    return response.data;
  } catch (error) {
    console.error('Erro detalhado ao realizar login:', error);
    
    // Exibir detalhes mais específicos do erro
    if (error.response) {
      // O servidor respondeu com um status de erro
      console.error('Status do erro:', error.response.status);
      console.error('Status text:', error.response.statusText);
      console.error('Headers:', error.response.headers);
      console.error('Dados do erro:', error.response.data);
      
      // Mensagem de erro mais detalhada
      const errorMessage = error.response.data?.error 
        ? `Erro: ${error.response.data.error}` 
        : `Erro do servidor: ${error.response.status} - ${error.response.statusText}`;
      
      throw new Error(errorMessage);
    } else if (error.request) {
      // A requisição foi feita mas não houve resposta
      console.error('Sem resposta do servidor. Dados da requisição:', error.request);
      console.error('Configuração da requisição:', error.config);
      
      throw new Error('O servidor não respondeu. Verifique sua conexão e tente novamente.');
    } else {
      // Ocorreu um erro durante a configuração da requisição
      console.error('Erro na configuração da requisição:', error.message);
      console.error('Stack:', error.stack);
    }
    
    throw new Error(`Falha ao comunicar com o servidor: ${error.message}`);
  }
};

/**
 * Realiza o logout do usuário
 * @returns {Promise} - Promessa com o resultado do logout
 */
export const logout = async () => {
  try {
    // Determinar endpoint de logout e chaves com base no modo
    const endpoint = IS_ADMIN ? 'logout' : 'logout';
    const tokenKey = IS_ADMIN ? 'admin_auth_token' : 'auth_token';
    const userDataKey = IS_ADMIN ? 'admin_user_data' : 'user_data';
    
    console.log('Tentando logout em:', `${API_URL}/${endpoint}`);
    const response = await apiClient.post(endpoint);
    console.log('Resposta do logout:', response.data);
    
    // Limpar dados de autenticação mesmo se a requisição falhar
    localStorage.removeItem(tokenKey);
    localStorage.removeItem(userDataKey);
    
    return response.data;
  } catch (error) {
    console.error('Erro ao realizar logout:', error);
    
    // Limpar dados de autenticação mesmo em caso de erro
    const tokenKey = IS_ADMIN ? 'admin_auth_token' : 'auth_token';
    const userDataKey = IS_ADMIN ? 'admin_user_data' : 'user_data';
    localStorage.removeItem(tokenKey);
    localStorage.removeItem(userDataKey);
    
    throw new Error('Falha ao comunicar com o servidor. Sessão encerrada localmente.');
  }
};

/**
 * Verifica se o usuário está autenticado
 * @returns {boolean} - true se estiver autenticado, false caso contrário
 */
export const isAuthenticated = () => {
  console.log('Verificando autenticação. Auth token:', getAuthToken());
  console.log('localStorage keys:', Object.keys(localStorage));
  
  // Em ambiente de desenvolvimento, permitir um bypass de depuração
  const env = (window.env && window.env.REACT_APP_ENV) || process.env.REACT_APP_ENV;
  if (env === 'development') {
    const debugBypass = localStorage.getItem('debug_bypass_auth');
    if (debugBypass === 'true') {
      console.log('MODO DEBUG: Bypass de autenticação ativo');
      return true;
    }
  }
  
  return !!getAuthToken();
};

/**
 * Obtém informações do usuário atual
 * @returns {Object|null} - Dados do usuário ou null se não estiver autenticado
 */
export const getCurrentUser = () => {
  const key = IS_ADMIN ? 'admin_user_data' : 'user_data';
  const userData = localStorage.getItem(key);
  return userData ? JSON.parse(userData) : null;
};

/**
 * Inicializa o banco de dados
 * @returns {Promise} - Promessa com o resultado da inicialização
 */
export const initializeDatabase = async () => {
  try {
    console.log('Inicializando banco de dados em:', `${API_URL}/init-db`);
    const response = await apiClient.get('init-db');
    console.log('Resposta da inicialização do DB:', response.data);
    return response.data;
  } catch (error) {
    console.error('Erro ao inicializar banco de dados:', error);
    if (error.response) {
      console.error('Status do erro:', error.response.status);
      console.error('Dados do erro:', error.response.data);
    } else if (error.request) {
      console.error('Sem resposta do servidor:', error.request);
    }
    throw new Error('Falha ao inicializar o banco de dados');
  }
};

/**
 * Atualiza os dados do usuário no servidor
 * @returns {Promise} - Promessa com as informações atualizadas do usuário
 */
export const refreshUserData = async () => {
  try {
    // Diferentes endpoints e chaves para regular vs admin
    const endpoint = IS_ADMIN ? 'me' : 'me';
    const userDataKey = IS_ADMIN ? 'admin_user_data' : 'user_data';
    const userField = IS_ADMIN ? 'admin' : 'user';
    
    console.log('Obtendo dados do usuário em:', `${API_URL}/${endpoint}`);
    const response = await apiClient.get(endpoint);
    console.log('Resposta dos dados do usuário:', response.data);
    
    if (response.data.success) {
      // Atualizar dados do usuário no localStorage
      localStorage.setItem(userDataKey, JSON.stringify(response.data[userField]));
      return response.data[userField];
    }
    
    return null;
  } catch (error) {
    console.error('Erro ao obter dados do usuário:', error);
    if (error.response) {
      console.error('Status do erro:', error.response.status);
      console.error('Dados do erro:', error.response.data);
    } else if (error.request) {
      console.error('Sem resposta do servidor:', error.request);
    }
    return null;
  }
};

// ============== FUNÇÕES ESPECÍFICAS DO ADMIN ==============
// Estas funções só estão disponíveis quando IS_ADMIN === true

/**
 * Lista todos os tenants disponíveis no sistema
 * @returns {Promise} - Promessa com a lista de tenants
 */
export const listTenants = async () => {
  if (!IS_ADMIN) return { success: false, error: 'Funcionalidade disponível apenas para administradores' };
  
  try {
    const response = await apiClient.get('tenants');
    return response.data;
  } catch (error) {
    console.error('Erro ao listar tenants:', error);
    throw new Error('Falha ao obter lista de tenants');
  }
};

/**
 * Obtém detalhes de um tenant específico
 * @param {number} tenantId - ID do tenant
 * @returns {Promise} - Promessa com os detalhes do tenant
 */
export const getTenantDetails = async (tenantId) => {
  if (!IS_ADMIN) return { success: false, error: 'Funcionalidade disponível apenas para administradores' };
  
  try {
    const response = await apiClient.get(`tenants/${tenantId}`);
    return response.data;
  } catch (error) {
    console.error(`Erro ao obter detalhes do tenant ${tenantId}:`, error);
    throw new Error('Falha ao obter detalhes do tenant');
  }
};

/**
 * Cria um novo tenant
 * @param {Object} tenantData - Dados do tenant (name, slug, etc.)
 * @returns {Promise} - Promessa com o tenant criado
 */
export const createTenant = async (tenantData) => {
  if (!IS_ADMIN) return { success: false, error: 'Funcionalidade disponível apenas para administradores' };
  
  try {
    const response = await apiClient.post('tenants', tenantData);
    return response.data;
  } catch (error) {
    console.error('Erro ao criar tenant:', error);
    if (error.response && error.response.data && error.response.data.error) {
      throw new Error(error.response.data.error);
    }
    throw new Error('Falha ao criar tenant');
  }
};

/**
 * Atualiza um tenant existente
 * @param {number} tenantId - ID do tenant
 * @param {Object} tenantData - Dados atualizados do tenant
 * @returns {Promise} - Promessa com o tenant atualizado
 */
export const updateTenant = async (tenantId, tenantData) => {
  if (!IS_ADMIN) return { success: false, error: 'Funcionalidade disponível apenas para administradores' };
  
  try {
    const response = await apiClient.put(`tenants/${tenantId}`, tenantData);
    return response.data;
  } catch (error) {
    console.error(`Erro ao atualizar tenant ${tenantId}:`, error);
    if (error.response && error.response.data && error.response.data.error) {
      throw new Error(error.response.data.error);
    }
    throw new Error('Falha ao atualizar tenant');
  }
};

/**
 * Lista todos os usuários de um tenant
 * @param {number} tenantId - ID do tenant
 * @returns {Promise} - Promessa com a lista de usuários
 */
export const listTenantUsers = async (tenantId) => {
  if (!IS_ADMIN) return { success: false, error: 'Funcionalidade disponível apenas para administradores' };
  
  try {
    const response = await apiClient.get(`tenants/${tenantId}/users`);
    return response.data;
  } catch (error) {
    console.error(`Erro ao listar usuários do tenant ${tenantId}:`, error);
    throw new Error('Falha ao listar usuários do tenant');
  }
};

/**
 * Cria um novo usuário para um tenant
 * @param {number} tenantId - ID do tenant
 * @param {Object} userData - Dados do usuário
 * @returns {Promise} - Promessa com o usuário criado e senha gerada
 */
export const createTenantUser = async (tenantId, userData) => {
  if (!IS_ADMIN) return { success: false, error: 'Funcionalidade disponível apenas para administradores' };
  
  try {
    const response = await apiClient.post(`tenants/${tenantId}/users`, userData);
    return response.data;
  } catch (error) {
    console.error(`Erro ao criar usuário para tenant ${tenantId}:`, error);
    if (error.response && error.response.data && error.response.data.error) {
      throw new Error(error.response.data.error);
    }
    throw new Error('Falha ao criar usuário');
  }
};

/**
 * Atualiza um usuário de um tenant
 * @param {number} tenantId - ID do tenant
 * @param {number} userId - ID do usuário
 * @param {Object} userData - Dados atualizados do usuário
 * @returns {Promise} - Promessa com o usuário atualizado
 */
export const updateTenantUser = async (tenantId, userId, userData) => {
  if (!IS_ADMIN) return { success: false, error: 'Funcionalidade disponível apenas para administradores' };
  
  try {
    const response = await apiClient.put(`tenants/${tenantId}/users/${userId}`, userData);
    return response.data;
  } catch (error) {
    console.error(`Erro ao atualizar usuário ${userId} do tenant ${tenantId}:`, error);
    if (error.response && error.response.data && error.response.data.error) {
      throw new Error(error.response.data.error);
    }
    throw new Error('Falha ao atualizar usuário');
  }
};

/**
 * Redefine a senha de um usuário
 * @param {number} tenantId - ID do tenant
 * @param {number} userId - ID do usuário
 * @param {string|null} manualPassword - Senha manual (opcional)
 * @returns {Promise} - Promessa com a nova senha
 */
export const resetTenantUserPassword = async (tenantId, userId, manualPassword = null) => {
  if (!IS_ADMIN) return { success: false, error: 'Funcionalidade disponível apenas para administradores' };
  
  try {
    const data = manualPassword ? { manual_password: manualPassword } : {};
    const response = await apiClient.post(`tenants/${tenantId}/users/${userId}/reset-password`, data);
    return response.data;
  } catch (error) {
    console.error(`Erro ao redefinir senha do usuário ${userId} do tenant ${tenantId}:`, error);
    if (error.response && error.response.data && error.response.data.error) {
      throw new Error(error.response.data.error);
    }
    throw new Error('Falha ao redefinir senha');
  }
};